import { Head, Link } from '@inertiajs/react'
import { CalendarDays } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import type { Breadcrumb } from '@/types/shared'

type PracticeMatchSummary = {
  id: string
  scheduledAt: string
  homeType: 'Team' | 'School'
  homeName: string | null
  awayName: string | null
  location: string | null
}

type Props = {
  practiceMatches: PracticeMatchSummary[]
  breadcrumbs: Breadcrumb[]
}

export default function AdminPracticeMatchesIndex({ practiceMatches, breadcrumbs }: Props) {
  return (
    <AppShell>
      <Head title="練習試合一覧" />
      <PageHeader
        title="練習試合一覧"
        description="登録済みの練習試合（クラブチーム同士／高校同士）を管理します。"
        breadcrumbs={breadcrumbs}
        actions={
          <Button asChild size="sm">
            <Link href="/admin/practice_matches/new">新規登録</Link>
          </Button>
        }
      />

      {practiceMatches.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="h-8 w-8" />}
          title="練習試合はまだ登録されていません"
          description="「新規登録」から最初の練習試合を登録できます。"
        />
      ) : (
        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/5 text-sm text-white/80">
              <thead className="bg-white/5 text-xs uppercase tracking-wide text-white/60">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">日時</th>
                  <th className="px-6 py-4 text-left font-semibold">対戦種別</th>
                  <th className="px-6 py-4 text-left font-semibold">対戦カード</th>
                  <th className="px-6 py-4 text-left font-semibold">会場</th>
                  <th className="px-6 py-4" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {practiceMatches.map((match) => (
                  <tr key={match.id} className="hover:bg-white/5 transition">
                    <td className="px-6 py-4 text-white">{formatDateTime(match.scheduledAt)}</td>
                    <td className="px-6 py-4 text-white/70">
                      {match.homeType === 'Team' ? 'クラブチーム同士' : '高校同士'}
                    </td>
                    <td className="px-6 py-4 font-medium text-white">
                      {(match.homeName ?? '?') + ' vs ' + (match.awayName ?? '?')}
                    </td>
                    <td className="px-6 py-4 text-white/70">{match.location ?? '未設定'}</td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-4 text-sm">
                        <Link
                          href={`/admin/practice_matches/${match.id}`}
                          className="text-brand-200 hover:text-brand-100"
                        >
                          詳細
                        </Link>
                        <Link
                          href={`/admin/practice_matches/${match.id}/edit`}
                          className="text-brand-200 hover:text-brand-100"
                        >
                          編集
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </AppShell>
  )
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
